// SOUND-791 — what the create modal SENDS as intake `fields`, and therefore
// which fields are visible.
//
// The server (`validateIntakeValues`) decides visibility by running
// `evaluateVisibleWhen` — a strict `===` — against the RAW `fields` it was
// sent, BEFORE it coerces anything. So "visible on screen" only equals
// "kept on save" when the screen evaluates against that same payload. This
// module builds the payload and reads visibility off it, so the two cannot
// disagree:
//
//   1. Visibility is evaluated against the payload as sent, not the form's raw
//      state. A field the form still holds an answer for, but which is hidden,
//      is not sent — so nothing that depends on it can be visible either.
//   2. A number field's answer is sent as a NUMBER, so it `===` a number
//      condition (the editor now saves one — see `visibleWhenToApi`).
//   3. Legacy tolerance, no migration: a template saved before SOUND-791 may
//      hold a STRING condition ("5") on a number field. For a field referenced
//      by such a condition the answer is sent as the raw string instead, which
//      is exactly what the server compared before this change — so the old
//      condition keeps matching, on screen and on save. Re-saving the template
//      in the editor converts the condition to a number and ends the exception.
//
// Pure, pinned in intakeSubmission.test.js (no component harness, SOUND-679).

import { evaluateVisibleWhen, isIntakeValueEmpty } from "constants/ticketTemplates";

/**
 * A numeric string → its number; anything else unchanged (a non-numeric value
 * is left for the server's error to name). Shared by every predicate and
 * answer conversion — spawnWhen (SOUND-546), visibleWhen and intake answers.
 */
export function toNumberIfNumeric(v) {
  if (typeof v !== "string" || v.trim() === "") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

function hasStringValue(pred) {
  const v = pred.value;
  return Array.isArray(v) ? v.some((x) => typeof x === "string") : typeof v === "string";
}

/**
 * @param {Array} intakeFields - API-shape intake fields, in template order
 * @param {Object} values - the form's raw answers
 * @returns {{ fields: Object, visibleKeys: Set<string> }}
 *   `fields` is the request body's `fields`; `visibleKeys` the fields to show.
 */
export function buildIntakeSubmission(intakeFields = [], values = {}) {
  const raw = values && typeof values === "object" ? values : {};
  const list = (intakeFields || []).filter((f) => f && f.key);

  // Number fields that a legacy string condition compares against.
  const typeByKey = {};
  list.forEach((f) => (typeByKey[f.key] = f.type));
  const sendAsString = new Set();
  list.forEach((f) => {
    const p = f.visibleWhen;
    if (p && p.field && typeByKey[p.field] === "number" && hasStringValue(p)) sendAsString.add(p.field);
  });

  // Conditions reference EARLIER fields only (enforced by the editor and the
  // API), so the payload built so far is all the server's evaluation will see.
  const fields = {};
  const visibleKeys = new Set();
  for (const f of list) {
    if (!evaluateVisibleWhen(f.visibleWhen, fields)) continue;
    visibleKeys.add(f.key);
    const v = raw[f.key];
    if (!isIntakeValueEmpty(v)) {
      fields[f.key] = f.type === "number" && !sendAsString.has(f.key) ? toNumberIfNumeric(v) : v;
    }
    if (f.type === "select-other" && f.otherKey && !isIntakeValueEmpty(raw[f.otherKey])) {
      fields[f.otherKey] = raw[f.otherKey];
    }
  }
  return { fields, visibleKeys };
}
